/* eslint-disable max-lines */
import { CommonModule } from '@angular/common';
import type { QueryList, SimpleChanges } from '@angular/core';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  ViewChildren,
  output,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import type { FormArray, FormGroup } from '@angular/forms';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';

import type { Observable, Subscription } from 'rxjs';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { debounceTime, take } from 'rxjs/operators';

import { filterBoolean } from '@core/common-legacy';
import { InfoboxComponent } from '@core/components-infobox-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type { RequisitionOpening } from '@core/requisitions-types';
import { RequisitionOpeningReason, RequisitionOpeningStatus } from '@core/requisitions-types';
import { DatePickerComponent } from '@core/time-ui';
import type { SelectOption } from '@core/user-interface-legacy';
import {
  AddNewButtonComponent,
  BadgeComponent,
  BoxComponent,
  ButtonComponent,
  ConnectedOverlayDirective,
  DividerComponent,
  DropdownComponent,
  FormErrorDirective,
  FormFieldComponent,
  GroupComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  InputComponent,
  LabelComponent,
  OverlayOriginDirective,
  RtTooltip,
  SelectComponent,
  TruncateComponent,
} from '@core/user-interface-legacy';

import type { OpeningControls, AvailableFieldsMap, FormBox } from '../../../requisitions.types';
import { RequisitionFormFactory } from '../../../services/requisition-form.factory';
import { RequisitionsCommonTextsService } from '../../../services/requisitions-common-texts.service';
import { RequisitionsDialogsService } from '../../../services/requisitions-dialogs.service';

@Component({
  selector: 'rt-requisitions-openings-form',
  templateUrl: './requisitions-openings-form.component.html',
  styleUrls: ['./requisitions-openings-form.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AddNewButtonComponent,
    BadgeComponent,
    BoxComponent,
    ButtonComponent,
    CommonModule,
    ConnectedOverlayDirective,
    DatePickerComponent,
    DividerComponent,
    DropdownComponent,
    FormErrorDirective,
    FormFieldComponent,
    GroupComponent,
    HeadingComponent,
    IconButtonComponent,
    IconComponent,
    InputComponent,
    LabelComponent,
    OverlayOriginDirective,
    RouterModule,
    RtTooltip,
    SelectComponent,
    TranslatePipe,
    ReactiveFormsModule,
    TruncateComponent,
    InfoboxComponent,
  ],
})
export class RequisitionsOpeningsFormComponent {
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly requisitionsDialogsService = inject(RequisitionsDialogsService);
  private readonly requisitionsCommonTextsService = inject(RequisitionsCommonTextsService);
  private readonly i18n = inject(I18nService);

  public readonly openingReasonSelectOptions: SelectOption[] = [
    {
      label: this.i18n.translate(`app.requisitions.openings.reason.new_position`),
      value: RequisitionOpeningReason.NewPosition,
    },
    {
      label: this.i18n.translate(`app.requisitions.openings.reason.replacement`),
      value: RequisitionOpeningReason.Replacement,
    },
  ];
  public readonly RequisitionOpeningStatus: typeof RequisitionOpeningStatus =
    RequisitionOpeningStatus;
  public readonly openingErrorTranslations = {
    full: this.i18n.translate(
      'app.requisitions.details_page.openings.no_anticipated_date_reason_explanation_error',
    ),
    anticipatedStartDateReason: this.i18n.translate(
      'app.requisitions.details_page.openings.no_anticipated_date_and_reason_error',
    ),
    anticipatedStartDateExplanation: this.i18n.translate(
      'app.requisitions.details_page.openings.no_anticipated_date_and_explanation_error',
    ),
    reasonExplanation: this.i18n.translate(
      'app.requisitions.details_page.openings.no_reason_and_explanation_error',
    ),
    anticipatedStartDate: this.i18n.translate(
      'app.requisitions.details_page.openings.no_anticipated_date_error',
    ),
    reason: this.i18n.translate('app.requisitions.details_page.openings.no_reason_error'),
    explanation: this.i18n.translate('app.requisitions.details_page.openings.no_explanation_error'),
  };

  public readonly savedOpenings = input<RequisitionOpening[]>(undefined!);
  public readonly openingsFormArray = input<FormArray<FormGroup<OpeningControls>>>(undefined!);
  public readonly availableOpeningsFields = input<AvailableFieldsMap[FormBox.Openings]>(undefined!);
  public readonly isFormEditable = input<boolean>(undefined!);
  public readonly canOnlyDecreaseOpenings = input<boolean>(undefined!);
  public readonly canDeleteOpeningsAndUnassign = input<boolean>(undefined!);
  public readonly deletedOpeningsCandidatesCount = input<number>(undefined!);

  public forceOpeningsDelete = output<void>();
  public unassignCandidate = output<number>();
  public isEditingOpening = output<boolean>();

  public decreaseOpeningsSelectOptions: SelectOption[];

  public selectedOpeningIndex: number | null = null;
  public cachedEditingOpening: Partial<RequisitionOpening> | null;
  public anticipatedStartDatePickerOpen = false;
  public numberOfOpeningsControl = new FormControl('', {
    nonNullable: true,
    validators: [Validators.pattern('^[0-9]*$'), Validators.max(120)],
  });
  public openingsRequireOnlyNumber: boolean;
  public savedOpeningsCount: number;
  public filledOpenings: RequisitionOpening[] = [];

  private editingOpeningIndex$ = new BehaviorSubject<number | null>(null);
  private formFactory = new RequisitionFormFactory();
  private ngOnDestroy$ = new Subject<void>();
  private currentAddedOpeningsCount: number;
  private openingsArrayChangesSubscription: Subscription | null = null;

  @ViewChildren('openings', { read: ElementRef })
  private openingElementRefs: QueryList<ElementRef>;

  public ngOnInit(): void {
    this.editingOpeningIndex$.pipe(takeUntil(this.ngOnDestroy$.asObservable())).subscribe(index => {
      this.selectedOpeningIndex = index;
      this.isEditingOpening.emit(index !== null);
      if (index) this.scrollToOpeningItemView(index);
      this.cdRef.detectChanges();
    });
  }

  public ngAfterViewInit(): void {
    if (this.openingsRequireOnlyNumber) {
      this.subscribeToChangesInNumberOfOpenings();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.openingsFormArray?.currentValue) {
      this.subscribeToChangesInOpeningsArray();
    }

    if (changes.availableOpeningsFields?.currentValue) {
      const availableOpeningsFields = this.availableOpeningsFields();
      this.openingsRequireOnlyNumber =
        !availableOpeningsFields['anticipated_start_date'] &&
        !availableOpeningsFields['reason'] &&
        !availableOpeningsFields['explanation'];
      this.cdRef.detectChanges();
    }

    if (changes.savedOpenings?.currentValue) {
      this.savedOpeningsCount = this.savedOpenings().length;
      this.currentAddedOpeningsCount = this.savedOpeningsCount;
      this.filledOpenings = this.savedOpenings().filter(
        opening => opening.status === RequisitionOpeningStatus.Filled,
      );

      this.setInitialNumberOfOpeningsValue();
    }

    if (this.canOnlyDecreaseOpenings() && this.openingsRequireOnlyNumber)
      this.setDecreaseOpeningsSelectOptions();
  }

  public trackByIndex(index: number, _: any): number {
    return index;
  }

  public get openingsCounter(): string {
    return `${this.filledOpenings.length}/${this.currentAddedOpeningsCount}`;
  }

  public get isInvalid(): boolean {
    const openingsFormArray = this.openingsFormArray();
    return (
      openingsFormArray.hasValidator(Validators.required) &&
      (!openingsFormArray.length || openingsFormArray.invalid)
    );
  }

  public get showAsterisk(): boolean {
    return !this.openingsFormArray().length && this.isFormEditable();
  }

  public get showReduceNumberOfOpeningsInfobox(): boolean {
    return (
      this.canDeleteOpeningsAndUnassign() &&
      this.canOnlyDecreaseOpenings() &&
      this.openingsRequireOnlyNumber &&
      !this.deletedOpeningsCandidatesCount() &&
      this.savedOpeningsCount > 1
    );
  }

  public getPossibleDeletedCandidateData(openingId: number): {
    isRecentlyDeleted: boolean;
    isDeleted: boolean;
    openingId: number;
  } {
    const placement = this.savedOpenings().find(opening => opening.id === openingId)?.placement;
    return {
      isDeleted: !placement,
      isRecentlyDeleted: !!placement?.candidate.deletedAt,
      openingId,
    };
  }

  public displayAnticipatedStartDate(value: string): string {
    return this.requisitionsCommonTextsService.getFormattedAnticipatedStartDate(value);
  }

  public displayOpeningReason(value: string): string {
    return this.requisitionsCommonTextsService.getOpeningReason(value);
  }

  public addNewOpening(): void {
    this.addOpeningToFormArray(false);
    this.editingOpeningIndex$.next(this.openingsFormArray().length - 1);
    this.cdRef.detectChanges();
  }

  public editOpening(index: number): void {
    this.editingOpeningIndex$.next(index);
    const anticipatedStartDate = this.openingsFormArray()
      .at(index)
      .get('anticipatedStartDate')?.value;
    const reason = this.openingsFormArray().at(index).get('reason')?.value;
    const explanation = this.openingsFormArray().at(index).get('explanation')?.value;
    this.cachedEditingOpening = { anticipatedStartDate, reason, explanation };
    this.cdRef.detectChanges();
  }

  public copyOpening(index: number): void {
    const { id, ...copiedOpening } = this.openingsFormArray().at(index).value;
    const openingsNumber = this.openingsFormArray().length;
    this.addOpeningToFormArray(true);
    this.openingsFormArray().at(openingsNumber).patchValue(copiedOpening);
  }

  public deleteOpening(index: number, markOpeningsAsDirty = true) {
    if (this.canOnlyDecreaseOpenings()) {
      this.deleteOpeningConfirmation$(1)
        .pipe(filterBoolean)
        .subscribe(() => {
          this.handleRemoveOpeningInForm(index, markOpeningsAsDirty);
          this.forceOpeningsDelete.emit();
        });
    } else {
      this.handleRemoveOpeningInForm(index, markOpeningsAsDirty);
    }
  }

  public cancelOpening(index: number) {
    const isNewOpening = !this.cachedEditingOpening;
    if (!isNewOpening) {
      const { anticipatedStartDate, reason, explanation } = this.cachedEditingOpening!;
      this.openingsFormArray().at(index).patchValue({ anticipatedStartDate, reason, explanation });
    } else {
      this.openingsFormArray().removeAt(index);
    }
    this.clearSelectedOpeningData();
    this.cdRef.detectChanges();
  }

  public saveOpening(index: number, addNew: boolean) {
    this.openingsFormArray().at(index).patchValue({ saved: true });
    this.clearSelectedOpeningData();
    if (addNew) {
      this.addNewOpening();
    }
    this.cdRef.detectChanges();
  }

  public toggleStartDatePicker(): void {
    this.anticipatedStartDatePickerOpen = !this.anticipatedStartDatePickerOpen;
    this.cdRef.detectChanges();
  }

  public getOpeningErrorText(opening: FormGroup<OpeningControls>): string {
    const openingControls = opening.controls;
    if (
      openingControls.anticipatedStartDate?.invalid &&
      openingControls.reason?.invalid &&
      openingControls.explanation?.invalid
    ) {
      return this.openingErrorTranslations.full;
    }
    if (openingControls.anticipatedStartDate?.invalid && openingControls.reason?.invalid) {
      return this.openingErrorTranslations.anticipatedStartDateReason;
    }
    if (openingControls.anticipatedStartDate?.invalid && openingControls.explanation?.invalid) {
      return this.openingErrorTranslations.anticipatedStartDateExplanation;
    }
    if (openingControls.reason?.invalid && openingControls.explanation?.invalid) {
      return this.openingErrorTranslations.reasonExplanation;
    }
    if (openingControls.anticipatedStartDate?.invalid) {
      return this.openingErrorTranslations.anticipatedStartDate;
    }
    if (openingControls.reason?.invalid) {
      return this.openingErrorTranslations.reason;
    }
    if (openingControls.explanation?.invalid) {
      return this.openingErrorTranslations.explanation;
    }
    return '';
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  private clearSelectedOpeningData(): void {
    this.cachedEditingOpening = null;
    this.editingOpeningIndex$.next(null);
  }

  private propagateOpeningsNumberToArray(
    numberOfOpenings: string,
    markOpeningsAsDirty: boolean = true,
  ): void {
    const openOpenings = this.savedOpenings().filter(
      opening => opening.status !== RequisitionOpeningStatus.Filled,
    );
    this.openingsFormArray().clear();
    // At first fill formArray with "filled" openings and then with the rest from the "open" ones
    if (this.numberOfOpeningsControl.valid) {
      this.filledOpenings.forEach(filledOpening => this.addOpeningToFormArray(true, filledOpening));

      for (let i = 0; i < Number(numberOfOpenings) - this.filledOpenings.length; i++) {
        this.addOpeningToFormArray(true, openOpenings?.[i]);
      }
      if (markOpeningsAsDirty) this.openingsFormArray().markAsDirty();
    }
  }

  private addOpeningToFormArray(isSaved: boolean, opening?: RequisitionOpening): void {
    this.openingsFormArray().push(
      this.formFactory.buildOpening({
        availableOpeningsFields: this.availableOpeningsFields(),
        isSaved,
        opening,
      }),
    );
  }

  private subscribeToChangesInNumberOfOpenings(): void {
    this.numberOfOpeningsControl.valueChanges
      .pipe(debounceTime(100), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(value => {
        if (this.numberOfOpeningsControl.valid) {
          if (this.canOnlyDecreaseOpenings()) {
            const openingsToDeleteCount = this.savedOpeningsCount - Number(value);
            this.deleteOpeningConfirmation$(openingsToDeleteCount).subscribe(confirmed => {
              if (confirmed) {
                this.propagateOpeningsNumberToArray(value, false);
                this.forceOpeningsDelete.emit();
              } else {
                this.setInitialNumberOfOpeningsValue();
              }
            });
          } else {
            this.propagateOpeningsNumberToArray(value);
          }
        }
        this.cdRef.detectChanges();
      });
  }

  private subscribeToChangesInOpeningsArray(): void {
    if (this.openingsArrayChangesSubscription) {
      this.openingsArrayChangesSubscription.unsubscribe();
      this.openingsArrayChangesSubscription = null;
    }

    this.openingsArrayChangesSubscription = this.openingsFormArray()
      ?.valueChanges.pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(value => {
        const newCurrentAddedOpeningsCount = value.filter(opening => opening.saved).length;
        if (newCurrentAddedOpeningsCount !== this.currentAddedOpeningsCount) {
          this.currentAddedOpeningsCount = newCurrentAddedOpeningsCount;
          this.cdRef.detectChanges();
        }
      });
  }

  private setDecreaseOpeningsSelectOptions(): void {
    const decreaseOpeningsSelectOptions: SelectOption[] = [];
    const freeOpeningsCount = this.savedOpenings().filter(
      opening => opening.status !== RequisitionOpeningStatus.Filled,
    ).length;

    if (freeOpeningsCount === 0) {
      decreaseOpeningsSelectOptions.push({
        label: this.savedOpeningsCount.toString(),
        value: this.savedOpeningsCount.toString(),
      });
    } else {
      for (let i = this.savedOpeningsCount - freeOpeningsCount; i <= this.savedOpeningsCount; i++) {
        decreaseOpeningsSelectOptions.push({
          label: i.toString(),
          value: i.toString(),
        });
      }
    }

    this.decreaseOpeningsSelectOptions = decreaseOpeningsSelectOptions;
    this.cdRef.detectChanges();
  }

  private deleteOpeningConfirmation$(count: number): Observable<boolean> {
    return this.requisitionsDialogsService.confirmDeleteOpening$(count).pipe(take(1));
  }

  private handleRemoveOpeningInForm(index: number, markOpeningsAsDirty: boolean): void {
    this.openingsFormArray().removeAt(index);
    this.clearSelectedOpeningData();
    if (markOpeningsAsDirty) this.openingsFormArray().markAsDirty();
    this.cdRef.detectChanges();
  }

  private setInitialNumberOfOpeningsValue(): void {
    this.numberOfOpeningsControl.setValue(this.savedOpeningsCount.toString(), {
      emitEvent: false,
    });
  }

  private scrollToOpeningItemView(index: number) {
    const openingElement: HTMLElement = this.openingElementRefs.toArray()[index].nativeElement;
    openingElement.scrollIntoView({ behavior: 'smooth' });
  }
}
